import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useMemo } from "react";

/** Quota key for AI / message credits on the lite subscription payload. */
const AI_CREDITS_QUOTA_KEY = "jugl::ai_credits" as const;

export interface AIMessageCredits {
  /** Credits consumed in the current billing period. */
  usage: number;
  /**
   * Display allowance: plan quota plus purchased / rolled-over balance.
   * Falls back to the plan default while quota is still unknown.
   */
  allowed: number;
  /** Billing-period end date from the quota, if loaded. */
  renewsDate: string | undefined;
  /**
   * True when quota is loaded and usage has reached the actual allowance
   * (without the plan-default fallback). False while quota is unknown so
   * the UI does not flash a warning.
   */
  isOutOfCredits: boolean;
  /** True while the subscription payload is still loading. */
  isLoading: boolean;
}

/**
 * Reads workspace AI message-credit usage from the lite subscription quota.
 * `allowed` is `quota.allowed + quota.balance`, matching the credits tile.
 */
export const useAIMessageCredits = (): AIMessageCredits => {
  const { liteSubscription, isLoading, planDefaultCredits } =
    useSubscriptionV2();

  return useMemo(() => {
    const quota = liteSubscription?.quota?.[AI_CREDITS_QUOTA_KEY];
    const usage = quota?.usage ?? 0;
    const quotaTotal = (quota?.allowed ?? 0) + (quota?.balance ?? 0);

    return {
      usage,
      allowed: quotaTotal || planDefaultCredits,
      renewsDate: quota?.end_date,
      isOutOfCredits: !!quota && usage >= quotaTotal,
      isLoading,
    };
  }, [liteSubscription, isLoading, planDefaultCredits]);
};
